import { FC, ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { FormattedMessage, useIntl } from "react-intl";
import { Controller, FieldValues, UseFormReturn, useFormContext } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import { Box, Checkbox, FormControlLabel, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";

import { NumberInput, SelectInput, SwitchInput } from "@framework/mui-inputs-core";
import { PageHeader } from "@framework/mui-page-layout";
import {
  EquipmentStateDisplayMode,
  IUserDto,
  ISetInterfaceSettingsDto,
  ISetSchemePageOrderDto,
  ISetSelfRecorderSettingsDto,
  SelfRecorderMode,
  UserVersion,
  useUser,
} from "@framework/provider-user";
import { useApiCall } from "@framework/react-hooks";
import { FormWrapper } from "@framework/mui-form";
import {
  mergeDashboardModulesOrder,
  normalizeDashboardModulesView,
  normalizeStationTileAnalyticsRowsView,
  normalizeStationTileAnalyticsRowsVisibility,
  normalizeUserDataFormatSettings,
  normalizeUserMultiAnalysisSettings,
  sanitizeDashboardModulesOrder,
  sanitizeStationTileAnalyticsRowsOrder,
} from "@framework/types";

import { ALARM_COLOR_KEYS, DEFAULT_USER_ALARM_COLORS, type IUserAlarmColors } from "../../utils/alarm-colors";

import { DraggableTabList } from "./components/draggable-tab-list";
import { AlarmColorsSettings } from "./components/alarm-colors-settings";
import { ColumnCountSelector } from "./components/column-count-selector";
import { DataFormatSettings } from "./components/data-format-settings";
import { ProfileSettingsSectionTitle, ProfileSettingsTabs } from "./components/profile-settings-tabs";
import {
  MAIN_DASHBOARD_MODULE_LABEL_MESSAGE_IDS,
  filterMainDashboardModuleIdsForVersion,
  getMainDashboardModuleIdsForVersion,
} from "../dashboard/main-dashboard-modules-meta";
import {
  STATION_TILE_ANALYTICS_LABEL_MESSAGE_IDS,
  STATION_TILE_ANALYTICS_ROW_IDS,
} from "../dashboard/station-tile-analytics-meta";
import { IProfileDashboardModulesCheckedState, IProfileFormValues, IProfileTabListCheckedState } from "./types";
import {
  DEFAULT_PROFILE_SETTINGS_TAB,
  EQUIPMENT_STATE_DISPLAY_MODE_OPTIONS,
  PROFILE_SETTINGS_LAYOUT_MAX_WIDTH,
  isProfileSettingsTabId,
  MAX_RECORD_MODE_TIME_UPDATE,
  MIN_RECORD_MODE_TIME_UPDATE,
  POPUP_MSG_MODE_OPTIONS,
  PROFILE_TAB_FORM_NAME_MAP,
  PROFILE_TAB_LABEL_MAP,
  ProfileSettingsTabId,
} from "./constants";
import { sanitizeSchemePageView } from "../dashboard/tabs/scheme/utils/scheme-page-view";
import { validationSchema } from "./validation";
import { StyledContainer, StyledContentGrid, StyledProfileFormFields } from "./styled";

const EquipmentStateDisplayModeInput = ({ hideTitle = false }: { hideTitle?: boolean }): ReactElement => {
  const { control } = useFormContext<IProfileFormValues>();

  return (
    <Controller
      name="equipmentStateDisplayMode"
      control={control}
      render={({ field }) => (
        <>
          {!hideTitle ? (
            <Typography variant="subtitle1" sx={{ mt: 1, mb: 1 }}>
              <FormattedMessage id="pages.profile.equipmentStateDisplayMode" />
            </Typography>
          ) : null}
          <ToggleButtonGroup
            exclusive
            value={field.value ?? EquipmentStateDisplayMode.GOST}
            onChange={(_event, value) => {
              if (value !== null) {
                field.onChange(value);
              }
            }}
            sx={{
              mb: 3,
              width: "auto",
              maxWidth: "100%",
              "& .MuiToggleButton-root": {
                textTransform: "none",
                fontWeight: 500,
              },
            }}
          >
            <ToggleButton value={EQUIPMENT_STATE_DISPLAY_MODE_OPTIONS.GOST}>
              <FormattedMessage id="pages.profile.equipmentStateDisplayModeGost" />
            </ToggleButton>
            <ToggleButton value={EQUIPMENT_STATE_DISPLAY_MODE_OPTIONS.TCI}>
              <FormattedMessage id="pages.profile.equipmentStateDisplayModeTci" />
            </ToggleButton>
            <ToggleButton value={EQUIPMENT_STATE_DISPLAY_MODE_OPTIONS.MODE}>
              <FormattedMessage id="pages.profile.equipmentStateDisplayModeMode" />
            </ToggleButton>
          </ToggleButtonGroup>
        </>
      )}
    />
  );
};

const SelfRecorderSettingsInputs = (): ReactElement => {
  const { control, watch } = useFormContext<IProfileFormValues>();
  const { formatMessage } = useIntl();
  const user = useUser<IUserDto>();
  const canChangeSelfRecorderMode = user.profile.version !== UserVersion.BASE;
  const mode = canChangeSelfRecorderMode ? watch("selfRecorderMode") : SelfRecorderMode.INTERVAL;

  return (
    <>
      {canChangeSelfRecorderMode ? (
        <Controller
          name="selfRecorderMode"
          control={control}
          render={({ field }) => (
            <>
              <Typography variant="subtitle1" sx={{ mt: 1, mb: 1 }}>
                <FormattedMessage id="pages.profile.selfRecorderMode" />
              </Typography>
              <ToggleButtonGroup
                exclusive
                value={field.value ?? SelfRecorderMode.INTERVAL}
                onChange={(_event, value) => {
                  if (value !== null) {
                    field.onChange(value);
                  }
                }}
                sx={{ mb: 2 }}
              >
                <ToggleButton value={SelfRecorderMode.INTERVAL}>
                  <FormattedMessage id="pages.profile.selfRecorderModeInterval" />
                </ToggleButton>
                <ToggleButton disabled value={SelfRecorderMode.SUBSCRIBE}>
                  <FormattedMessage id="pages.profile.selfRecorderModeSubscribe" />
                </ToggleButton>
              </ToggleButtonGroup>
            </>
          )}
        />
      ) : null}
      <NumberInput
        name="recordModeTimeUpdate"
        variant="outlined"
        defVal={MIN_RECORD_MODE_TIME_UPDATE}
        disabled={mode === SelfRecorderMode.SUBSCRIBE}
        inputProps={{ min: MIN_RECORD_MODE_TIME_UPDATE, max: MAX_RECORD_MODE_TIME_UPDATE }}
        helperText={formatMessage({ id: "form.placeholders.recordModeTimeUpdate" })}
        placeholder={formatMessage({ id: "pages.profile.recordModeTimeUpdate" })}
        label={<FormattedMessage id={"pages.profile.recordModeTimeUpdate"} />}
      />
    </>
  );
};

interface IProfileSettingsCheckboxProps {
  name:
    | "multiAnalysis.showStandardTemplates"
    | "multiAnalysis.showPublicTemplates"
    | "multiAnalysis.configureTemplateFiltersManually";
  labelId: string;
}

const ProfileSettingsCheckbox = ({ name, labelId }: IProfileSettingsCheckboxProps): ReactElement => {
  const { control } = useFormContext<IProfileFormValues>();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(field.value)}
              onChange={event => field.onChange(event.target.checked)}
              inputRef={field.ref}
            />
          }
          label={<FormattedMessage id={labelId} />}
        />
      )}
    />
  );
};

const ALARM_COLOR_KEYS_FOR_SUBMIT = ALARM_COLOR_KEYS;
const PROFILE_AT_LEAST_ONE_TAB_ERROR_MESSAGE_ID = "pages.profile.errorFormAtLeastOne";
const PROFILE_AT_LEAST_ONE_DASHBOARD_MODULE_ERROR_MESSAGE_ID = "pages.profile.errorFormAtLeastOneDashboardModule";
const PROFILE_AT_LEAST_ONE_STATION_TILE_ANALYTICS_ERROR_MESSAGE_ID =
  "pages.profile.errorFormAtLeastOneStationTileAnalytics";
const PROFILE_SETTINGS_SAVED_MESSAGE_ID = "pages.profile.settingsSaved";

function normalizeAlarmColorsForSubmit(ac: IUserAlarmColors): IUserAlarmColors {
  const out = { ...DEFAULT_USER_ALARM_COLORS };
  for (const k of ALARM_COLOR_KEYS_FOR_SUBMIT) {
    const v = ac[k]?.trim() ?? "";
    if (/^#[0-9A-Fa-f]{6}$/i.test(v)) {
      out[k] = v.toLowerCase();
    } else if (/^[0-9A-Fa-f]{6}$/i.test(v)) {
      out[k] = `#${v.toLowerCase()}`;
    }
  }
  return out;
}

function normalizeRecordModeTimeUpdate(value: unknown): number {
  const raw = Number(value);
  if (!Number.isFinite(raw)) {
    return MIN_RECORD_MODE_TIME_UPDATE;
  }
  return Math.max(Math.min(raw, MAX_RECORD_MODE_TIME_UPDATE), MIN_RECORD_MODE_TIME_UPDATE);
}

function normalizePopupMsgMode(value: IProfileFormValues["popupMsgMode"]): number {
  const popupMsgMode = Number(value);
  return Number.isNaN(popupMsgMode) ? Number(POPUP_MSG_MODE_OPTIONS.ALWAYS) : popupMsgMode;
}

function normalizeEquipmentStateDisplayMode(
  value: IProfileFormValues["equipmentStateDisplayMode"] | null | undefined,
): EquipmentStateDisplayMode {
  return value ?? Number(EQUIPMENT_STATE_DISPLAY_MODE_OPTIONS.GOST);
}

function hasActiveSchemeTab(checkedState: IProfileTabListCheckedState): boolean {
  return Object.values(checkedState).some(Boolean);
}

function hasActiveDashboardModule(checkedState: IProfileDashboardModulesCheckedState): boolean {
  return Object.values(checkedState).some(Boolean);
}

function hasActiveStationTileAnalyticsRow(checkedState: IProfileDashboardModulesCheckedState): boolean {
  return Object.values(checkedState).some(Boolean);
}

function buildInterfaceSettingsSubmitValues(
  values: IProfileFormValues,
  schemeCheckedState: IProfileTabListCheckedState,
  dashboardOrder: string[],
  dashboardCheckedState: IProfileDashboardModulesCheckedState,
  dashboardModuleIds: readonly string[],
  previousModulesVisibility: Record<string, boolean>,
  stationTileAnalyticsOrder: string[],
  stationTileAnalyticsCheckedState: IProfileDashboardModulesCheckedState,
): ISetInterfaceSettingsDto {
  const dashboardModulesVisibility = { ...previousModulesVisibility };

  for (const id of dashboardOrder) {
    dashboardModulesVisibility[id] = Boolean(dashboardCheckedState[id]);
  }

  const stationTileAnalyticsRowsVisibility: Record<string, boolean> = {};
  for (const id of STATION_TILE_ANALYTICS_ROW_IDS) {
    stationTileAnalyticsRowsVisibility[id] = Boolean(stationTileAnalyticsCheckedState[id]);
  }

  return {
    showTabsSeparately: values.showTabsSeparately,
    showTrainScheme: schemeCheckedState.showTrainScheme,
    showTableData: schemeCheckedState.showTableData,
    showStatusAndForecasting: schemeCheckedState.showStatusAndForecasting,
    showMeasureData: schemeCheckedState.showMeasureData,
    openTrainSchemeInNewWindow: values.openTrainSchemeInNewWindow,
    showMultitrendScales: values.showMultitrendScales,
    recordModeTimeUpdate: normalizeRecordModeTimeUpdate(values.recordModeTimeUpdate),
    popupMsgMode: normalizePopupMsgMode(values.popupMsgMode),
    equipmentStateDisplayMode: normalizeEquipmentStateDisplayMode(values.equipmentStateDisplayMode),
    dataFormat: normalizeUserDataFormatSettings(values.dataFormat),
    multiAnalysis: normalizeUserMultiAnalysisSettings(values.multiAnalysis),
    signalUnacknowledgedEvents: values.signalUnacknowledgedEvents,
    additionalFaultAlarm: values.additionalFaultAlarm,
    useDefaultAlarmColors: values.useDefaultAlarmColors,
    alarmColors: normalizeAlarmColorsForSubmit(values.alarmColors),
    dashboardModulesView: normalizeDashboardModulesView(dashboardOrder.join(","), dashboardModuleIds),
    dashboardModulesVisibility,
    stationTileAnalyticsRowsView: normalizeStationTileAnalyticsRowsView(
      stationTileAnalyticsOrder.join(","),
      STATION_TILE_ANALYTICS_ROW_IDS,
    ),
    stationTileAnalyticsRowsVisibility,
  };
}

function buildSchemePageOrderSubmitValues(order: string[]): ISetSchemePageOrderDto {
  return { schemePageView: sanitizeSchemePageView(order) };
}

export const Profile: FC = () => {
  const user = useUser<IUserDto>();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { tab: tabParam } = useParams<{ tab?: string }>();

  const activeTab: ProfileSettingsTabId = isProfileSettingsTabId(tabParam) ? tabParam : DEFAULT_PROFILE_SETTINGS_TAB;

  useEffect(() => {
    if (!isProfileSettingsTabId(tabParam)) {
      navigate(`/profile/${DEFAULT_PROFILE_SETTINGS_TAB}`, { replace: true });
    }
  }, [navigate, tabParam]);

  const handleSettingsTabChange = useCallback(
    (tab: ProfileSettingsTabId) => {
      navigate(`/profile/${tab}`);
    },
    [navigate],
  );

  const interfaceSettings = user.profile.interfaceSettings;
  const selfRecorderSettings = user.profile.selfRecorderSettings;
  const availableDashboardModuleIds = useMemo(
    () => getMainDashboardModuleIdsForVersion(user.profile.version),
    [user.profile.version],
  );

  const initialOrder = useMemo(
    () => sanitizeSchemePageView(interfaceSettings.schemePageView),
    [interfaceSettings.schemePageView],
  );
  const initialCheckedState = useMemo<IProfileTabListCheckedState>(
    () => ({
      showTrainScheme: interfaceSettings.showTrainScheme,
      showStatusAndForecasting: interfaceSettings.showStatusAndForecasting,
      showTableData: interfaceSettings.showTableData,
      showMeasureData: interfaceSettings.showMeasureData,
    }),
    [
      interfaceSettings.showMeasureData,
      interfaceSettings.showStatusAndForecasting,
      interfaceSettings.showTableData,
      interfaceSettings.showTrainScheme,
    ],
  );

  const [currentOrder, setCurrentOrder] = useState<string[]>(initialOrder);
  const [currentCheckedState, setCurrentCheckedState] = useState<IProfileTabListCheckedState>(initialCheckedState);

  const initialDashboardOrder = useMemo(
    () =>
      filterMainDashboardModuleIdsForVersion(
        sanitizeDashboardModulesOrder(interfaceSettings.dashboardModulesView, availableDashboardModuleIds),
        user.profile.version,
      ),
    [availableDashboardModuleIds, interfaceSettings.dashboardModulesView, user.profile.version],
  );

  const initialDashboardCheckedState = useMemo<IProfileDashboardModulesCheckedState>(() => {
    const orderIds = filterMainDashboardModuleIdsForVersion(
      mergeDashboardModulesOrder(
        sanitizeDashboardModulesOrder(interfaceSettings.dashboardModulesView, availableDashboardModuleIds),
        availableDashboardModuleIds,
      ),
      user.profile.version,
    );
    const vis = interfaceSettings.dashboardModulesVisibility ?? {};
    const out: IProfileDashboardModulesCheckedState = {};
    for (const id of orderIds) {
      out[id] = vis[id] !== false;
    }
    return out;
  }, [
    availableDashboardModuleIds,
    interfaceSettings.dashboardModulesView,
    interfaceSettings.dashboardModulesVisibility,
    user.profile.version,
  ]);

  const [currentDashboardOrder, setCurrentDashboardOrder] = useState<string[]>(initialDashboardOrder);
  const [currentDashboardCheckedState, setCurrentDashboardCheckedState] =
    useState<IProfileDashboardModulesCheckedState>(initialDashboardCheckedState);

  const initialStationTileAnalyticsOrder = useMemo(
    () =>
      sanitizeStationTileAnalyticsRowsOrder(
        interfaceSettings.ts_scheme?.stationTileAnalyticsRowsView,
        STATION_TILE_ANALYTICS_ROW_IDS,
      ),
    [interfaceSettings.ts_scheme?.stationTileAnalyticsRowsView],
  );

  const initialStationTileAnalyticsCheckedState = useMemo<IProfileDashboardModulesCheckedState>(() => {
    const orderIds = sanitizeStationTileAnalyticsRowsOrder(
      interfaceSettings.ts_scheme?.stationTileAnalyticsRowsView,
      STATION_TILE_ANALYTICS_ROW_IDS,
    );
    const vis = normalizeStationTileAnalyticsRowsVisibility(
      interfaceSettings.ts_scheme?.stationTileAnalyticsRowsVisibility,
    );
    const out: IProfileDashboardModulesCheckedState = {};
    for (const id of orderIds) {
      out[id] = vis[id as keyof typeof vis] === true;
    }
    return out;
  }, [
    interfaceSettings.ts_scheme.stationTileAnalyticsRowsView,
    interfaceSettings.ts_scheme.stationTileAnalyticsRowsVisibility,
  ]);

  const [currentStationTileAnalyticsOrder, setCurrentStationTileAnalyticsOrder] = useState<string[]>(
    initialStationTileAnalyticsOrder,
  );
  const [currentStationTileAnalyticsCheckedState, setCurrentStationTileAnalyticsCheckedState] =
    useState<IProfileDashboardModulesCheckedState>(initialStationTileAnalyticsCheckedState);

  const showValidationFailedSnackbar = useCallback((): void => {
    enqueueSnackbar(formatMessage({ id: PROFILE_AT_LEAST_ONE_TAB_ERROR_MESSAGE_ID }), { variant: "error" });
  }, [formatMessage]);

  const showSettingsSavedSnackbar = useCallback((): void => {
    enqueueSnackbar(formatMessage({ id: PROFILE_SETTINGS_SAVED_MESSAGE_ID }), { variant: "success" });
  }, [formatMessage]);

  const showDashboardValidationFailedSnackbar = useCallback((): void => {
    enqueueSnackbar(formatMessage({ id: PROFILE_AT_LEAST_ONE_DASHBOARD_MODULE_ERROR_MESSAGE_ID }), {
      variant: "error",
    });
  }, [formatMessage]);

  const showStationTileAnalyticsValidationFailedSnackbar = useCallback((): void => {
    enqueueSnackbar(formatMessage({ id: PROFILE_AT_LEAST_ONE_STATION_TILE_ANALYTICS_ERROR_MESSAGE_ID }), {
      variant: "error",
    });
  }, [formatMessage]);

  const { fn: saveProfileSettingsFn } = useApiCall(
    async (
      _api,
      interfaceSettingsValues: ISetInterfaceSettingsDto,
      schemePageOrderValues: ISetSchemePageOrderDto,
      selfRecorderSettingsValues: ISetSelfRecorderSettingsDto,
    ): Promise<boolean> => {
      await user.runSettingsUpdate(async () => {
        await user.setSchemePageOrder(schemePageOrderValues);
        await user.setInterfaceSettings(interfaceSettingsValues);
        await user.setSelfRecorderSettings(selfRecorderSettingsValues);
        await user.getProfile();
      });
      return true;
    },
    { success: false },
  );

  const handleSubmit = useCallback(
    async (values: IProfileFormValues, form?: UseFormReturn<FieldValues, any>): Promise<void> => {
      if (!hasActiveSchemeTab(currentCheckedState)) {
        showValidationFailedSnackbar();
        return;
      }

      if (!hasActiveDashboardModule(currentDashboardCheckedState)) {
        showDashboardValidationFailedSnackbar();
        return;
      }

      if (!hasActiveStationTileAnalyticsRow(currentStationTileAnalyticsCheckedState)) {
        showStationTileAnalyticsValidationFailedSnackbar();
        return;
      }

      const settingsSaved = await saveProfileSettingsFn(
        form,
        buildInterfaceSettingsSubmitValues(
          values,
          currentCheckedState,
          currentDashboardOrder,
          currentDashboardCheckedState,
          availableDashboardModuleIds,
          interfaceSettings.dashboardModulesVisibility ?? {},
          currentStationTileAnalyticsOrder,
          currentStationTileAnalyticsCheckedState,
        ),
        buildSchemePageOrderSubmitValues(currentOrder),
        {
          value: selfRecorderSettings.value,
          range: selfRecorderSettings.range,
          mode:
            user.profile.version !== UserVersion.BASE && values.selfRecorderMode === SelfRecorderMode.SUBSCRIBE
              ? SelfRecorderMode.SUBSCRIBE
              : SelfRecorderMode.INTERVAL,
        },
      );

      if (settingsSaved) {
        showSettingsSavedSnackbar();
      }
    },
    [
      currentCheckedState,
      currentDashboardCheckedState,
      currentDashboardOrder,
      currentOrder,
      currentStationTileAnalyticsCheckedState,
      currentStationTileAnalyticsOrder,
      availableDashboardModuleIds,
      interfaceSettings.dashboardModulesVisibility,
      interfaceSettings.ts_scheme.stationTileAnalyticsRowsVisibility,
      selfRecorderSettings.range,
      selfRecorderSettings.value,
      user.profile.version,
      saveProfileSettingsFn,
      showDashboardValidationFailedSnackbar,
      showSettingsSavedSnackbar,
      showStationTileAnalyticsValidationFailedSnackbar,
      showValidationFailedSnackbar,
    ],
  );

  useEffect(() => {
    setCurrentOrder(initialOrder);
    setCurrentCheckedState(initialCheckedState);
    setCurrentDashboardOrder(initialDashboardOrder);
    setCurrentDashboardCheckedState(initialDashboardCheckedState);
    setCurrentStationTileAnalyticsOrder(initialStationTileAnalyticsOrder);
    setCurrentStationTileAnalyticsCheckedState(initialStationTileAnalyticsCheckedState);
  }, [
    initialCheckedState,
    initialDashboardCheckedState,
    initialDashboardOrder,
    initialOrder,
    initialStationTileAnalyticsCheckedState,
    initialStationTileAnalyticsOrder,
  ]);

  const initialValues = useMemo(
    () => ({
      showTableData: interfaceSettings.showTableData,
      showMeasureData: interfaceSettings.showMeasureData,
      showTrainScheme: interfaceSettings.showTrainScheme,
      showStatusAndForecasting: interfaceSettings.showStatusAndForecasting,
      showTabsSeparately: interfaceSettings.showTabsSeparately,
      openTrainSchemeInNewWindow: interfaceSettings.openTrainSchemeInNewWindow,
      showMultitrendScales: interfaceSettings.showMultitrendScales ?? false,
      recordModeTimeUpdate:
        typeof selfRecorderSettings?.upd === "number" && Number.isFinite(selfRecorderSettings.upd)
          ? selfRecorderSettings.upd
          : MIN_RECORD_MODE_TIME_UPDATE,
      selfRecorderMode:
        user.profile.version !== UserVersion.BASE && selfRecorderSettings?.mode === SelfRecorderMode.SUBSCRIBE
          ? SelfRecorderMode.SUBSCRIBE
          : SelfRecorderMode.INTERVAL,
      popupMsgMode: String(interfaceSettings.popupMsgMode ?? POPUP_MSG_MODE_OPTIONS.ALWAYS),
      equipmentStateDisplayMode:
        interfaceSettings.equipmentStateDisplayMode ?? Number(EQUIPMENT_STATE_DISPLAY_MODE_OPTIONS.GOST),
      dataFormat: normalizeUserDataFormatSettings(interfaceSettings.dataFormat),
      multiAnalysis: normalizeUserMultiAnalysisSettings(interfaceSettings.multiAnalysis),
      signalUnacknowledgedEvents: interfaceSettings.ts_scheme.signalUnacknowledgedEvents,
      additionalFaultAlarm: interfaceSettings.ts_scheme.additionalFaultAlarm,
      useDefaultAlarmColors: interfaceSettings.useDefaultAlarmColors ?? true,
      alarmColors: { ...DEFAULT_USER_ALARM_COLORS, ...(interfaceSettings.alarmColors ?? {}) },
    }),
    [interfaceSettings, selfRecorderSettings, user.profile.version],
  );

  const draggableTabListKey = useMemo(
    () =>
      [
        sanitizeSchemePageView(interfaceSettings.schemePageView).join(""),
        interfaceSettings.showTrainScheme ? "1" : "0",
        interfaceSettings.showStatusAndForecasting ? "1" : "0",
        interfaceSettings.showTableData ? "1" : "0",
        interfaceSettings.showMeasureData ? "1" : "0",
      ].join("_"),
    [
      interfaceSettings.schemePageView,
      interfaceSettings.showMeasureData,
      interfaceSettings.showStatusAndForecasting,
      interfaceSettings.showTableData,
      interfaceSettings.showTrainScheme,
    ],
  );

  const draggableDashboardModulesKey = useMemo(() => {
    const orderFingerprint = filterMainDashboardModuleIdsForVersion(
      sanitizeDashboardModulesOrder(interfaceSettings.dashboardModulesView, availableDashboardModuleIds),
      user.profile.version,
    ).join(",");
    return `${orderFingerprint}|${JSON.stringify(interfaceSettings.dashboardModulesVisibility ?? {})}`;
  }, [
    availableDashboardModuleIds,
    interfaceSettings.dashboardModulesView,
    interfaceSettings.dashboardModulesVisibility,
    user.profile.version,
  ]);

  const draggableStationTileAnalyticsKey = useMemo(() => {
    const orderFingerprint = sanitizeStationTileAnalyticsRowsOrder(
      interfaceSettings.ts_scheme?.stationTileAnalyticsRowsView,
      STATION_TILE_ANALYTICS_ROW_IDS,
    ).join(",");
    return `${orderFingerprint}|${JSON.stringify(
      interfaceSettings.ts_scheme?.stationTileAnalyticsRowsVisibility ?? {},
    )}`;
  }, [
    interfaceSettings.ts_scheme?.stationTileAnalyticsRowsView,
    interfaceSettings.ts_scheme?.stationTileAnalyticsRowsVisibility,
  ]);

  const profileSettingsKey = useMemo(
    () =>
      [
        interfaceSettings.useDefaultAlarmColors ?? true,
        interfaceSettings.showMultitrendScales ?? false,
        JSON.stringify(interfaceSettings.alarmColors ?? {}),
        JSON.stringify(normalizeUserDataFormatSettings(interfaceSettings.dataFormat)),
        JSON.stringify(normalizeUserMultiAnalysisSettings(interfaceSettings.multiAnalysis)),
        selfRecorderSettings?.mode ?? SelfRecorderMode.INTERVAL,
        selfRecorderSettings?.upd ?? MIN_RECORD_MODE_TIME_UPDATE,
      ].join("|"),
    [
      interfaceSettings.useDefaultAlarmColors,
      interfaceSettings.showMultitrendScales,
      interfaceSettings.alarmColors,
      interfaceSettings.dataFormat,
      interfaceSettings.multiAnalysis,
      selfRecorderSettings?.mode,
      selfRecorderSettings?.upd,
    ],
  );

  const settingsTabPanels = useMemo(
    () => ({
      general: (
        <>
          <ProfileSettingsSectionTitle messageId="pages.profile.tabSelfRecorder" />
          <Box sx={{ maxWidth: 400 }}>
            <SelfRecorderSettingsInputs />
            <SelectInput
              name="popupMsgMode"
              variant="outlined"
              options={POPUP_MSG_MODE_OPTIONS}
              label={<FormattedMessage id={"pages.profile.popupMsgMode"} />}
            />
          </Box>
          <ProfileSettingsSectionTitle messageId="pages.profile.tabEquipment" />
          <EquipmentStateDisplayModeInput hideTitle />
          <ProfileSettingsSectionTitle messageId="pages.profile.titleDashboardModulesSettings" />
          <DraggableTabList
            key={draggableDashboardModulesKey}
            initialOrder={initialDashboardOrder}
            initialCheckedState={initialDashboardCheckedState}
            tabLabelMessageIdById={MAIN_DASHBOARD_MODULE_LABEL_MESSAGE_IDS}
            useTabIdAsCheckboxName
            onOrderChange={setCurrentDashboardOrder}
            onCheckedChange={setCurrentDashboardCheckedState}
            onValidationFailed={showDashboardValidationFailedSnackbar}
          />
        </>
      ),
      scheme: (
        <>
          <ProfileSettingsSectionTitle messageId="pages.profile.titleSchemeSettings" />
          <DraggableTabList<IProfileTabListCheckedState>
            key={draggableTabListKey}
            initialOrder={initialOrder}
            initialCheckedState={initialCheckedState}
            tabLabelMessageIdById={PROFILE_TAB_LABEL_MAP}
            tabCheckboxNameById={PROFILE_TAB_FORM_NAME_MAP}
            onOrderChange={setCurrentOrder}
            onCheckedChange={setCurrentCheckedState}
            onValidationFailed={showValidationFailedSnackbar}
          />
          <Box sx={{ mt: 3 }}>
            <ColumnCountSelector />
          </Box>
          <Box sx={{ mt: 3 }}>
            <ProfileSettingsSectionTitle messageId="pages.profile.titleStationTileAnalyticsSettings" />
            <DraggableTabList
              key={draggableStationTileAnalyticsKey}
              initialOrder={initialStationTileAnalyticsOrder}
              initialCheckedState={initialStationTileAnalyticsCheckedState}
              tabLabelMessageIdById={STATION_TILE_ANALYTICS_LABEL_MESSAGE_IDS}
              useTabIdAsCheckboxName
              maxCheckedCount={5}
              onOrderChange={setCurrentStationTileAnalyticsOrder}
              onCheckedChange={setCurrentStationTileAnalyticsCheckedState}
              onValidationFailed={showStationTileAnalyticsValidationFailedSnackbar}
            />
          </Box>
          <Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 0 }}>
            <SwitchInput
              name="showTabsSeparately"
              label={<FormattedMessage id={"pages.profile.titleShowTabsSeparately"} />}
            />
            <SwitchInput
              name="openTrainSchemeInNewWindow"
              label={<FormattedMessage id={"pages.profile.openTrainSchemeInNewWindow"} />}
            />
            <SwitchInput
              name="signalUnacknowledgedEvents"
              label={<FormattedMessage id={"pages.profile.signalUnacknowledgedEvents"} />}
            />
            <SwitchInput
              name="additionalFaultAlarm"
              label={<FormattedMessage id={"pages.profile.additionalFaultAlarm"} />}
            />
          </Box>
        </>
      ),
      additional: (
        <>
          <SwitchInput
            name="showMultitrendScales"
            label={<FormattedMessage id="pages.profile.showMultitrendScales" />}
          />
          <Box sx={{ mt: 3 }}>
            <ProfileSettingsSectionTitle messageId="pages.profile.tabDataFormat" />
            <DataFormatSettings hideTitle />
          </Box>
          <Box sx={{ mt: 3 }}>
            <ProfileSettingsSectionTitle messageId="pages.profile.tabAlarmColors" />
            <AlarmColorsSettings hideTitle />
          </Box>
        </>
      ),
      multiAnalysis: (
        <>
          <SwitchInput
            name="multiAnalysis.showTrendsPanel"
            label={<FormattedMessage id="pages.profile.multiAnalysisShowTrendsPanel" />}
          />
          <ProfileSettingsSectionTitle messageId="pages.profile.multiAnalysisTemplatesVisibility" />
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5 }}>
            <ProfileSettingsCheckbox
              name="multiAnalysis.showStandardTemplates"
              labelId="pages.profile.multiAnalysisShowStandardTemplates"
            />
            <ProfileSettingsCheckbox
              name="multiAnalysis.showPublicTemplates"
              labelId="pages.profile.multiAnalysisShowPublicTemplates"
            />
          </Box>
          <Box sx={{ mt: 3 }}>
            <ProfileSettingsSectionTitle messageId="pages.profile.multiAnalysisTemplateEditing" />
            <ProfileSettingsCheckbox
              name="multiAnalysis.configureTemplateFiltersManually"
              labelId="pages.profile.multiAnalysisConfigureTemplateFiltersManually"
            />
          </Box>
        </>
      ),
    }),
    [
      draggableDashboardModulesKey,
      draggableStationTileAnalyticsKey,
      draggableTabListKey,
      formatMessage,
      initialCheckedState,
      initialDashboardCheckedState,
      initialDashboardOrder,
      initialOrder,
      initialStationTileAnalyticsCheckedState,
      initialStationTileAnalyticsOrder,
      showDashboardValidationFailedSnackbar,
      showStationTileAnalyticsValidationFailedSnackbar,
      showValidationFailedSnackbar,
    ],
  );

  return (
    <StyledContainer container>
      <StyledContentGrid item xs={12}>
        <PageHeader message="pages.profile.title" sx={{ mt: 0, mb: 2, flexShrink: 0, justifyContent: "center" }} />

        <FormWrapper
          key={profileSettingsKey}
          initialValues={initialValues}
          enableReinitialize={false}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
          submit="save"
          submitRequiresValidForm={false}
          data-testid="Profile"
          sx={[
            {
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              "& .MuiFormControlLabel-root": {
                margin: 0,
              },
              "& .MuiSwitch-root + .MuiFormControlLabel-label": {
                marginLeft: "8px",
              },
              "& > .MuiGrid-container": {
                flexShrink: 0,
                mt: 2,
                width: "100%",
                maxWidth: PROFILE_SETTINGS_LAYOUT_MAX_WIDTH,
                justifyContent: "center",
              },
            },
          ]}
        >
          <StyledProfileFormFields>
            <ProfileSettingsTabs
              activeTab={activeTab}
              onTabChange={handleSettingsTabChange}
              panels={settingsTabPanels}
            />
          </StyledProfileFormFields>
        </FormWrapper>
      </StyledContentGrid>
    </StyledContainer>
  );
};
